import { memo, useCallback } from 'react';
import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';

export interface SelectionOption {
    readonly label: string;
    readonly value: any;
    readonly disabled?: boolean;
}

export interface RadioItemGroupProps {
    readonly options: readonly SelectionOption[];
    readonly value: any;
    readonly onChange: (value: any) => void;
    readonly size?: 'small' | 'medium' | 'large';
    readonly fullWidth?: boolean;
    readonly className?: string;
    readonly disabled?: boolean;
}

interface RadioItemProps {
    readonly selected: boolean;
    readonly label: string;
    readonly size: 'small' | 'medium' | 'large';
    readonly disabled: boolean;
    readonly onClick: () => void;
}

const RadioItem = memo(function RadioItem({ selected, label, size, disabled, onClick }: RadioItemProps) {
    return (
        <button
            type="button"
            className={['radio-item', selected ? 'selected' : ''].filter(Boolean).join(' ')}
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={onClick}
        >
            <span className="radio-item__icon" aria-hidden="true">
                <span className="radio-item__icon--unchecked">
                    <RadioButtonUnchecked fontSize={size} />
                </span>
                <span className="radio-item__icon--checked">
                    <RadioButtonChecked fontSize={size} />
                </span>
            </span>
            <span className="radio-item__label">{label}</span>
        </button>
    );
});

const RadioItemGroup = memo(function RadioItemGroup({
    options,
    value,
    onChange,
    size = 'small',
    fullWidth = false,
    className = '',
    disabled = false
}: RadioItemGroupProps) {
    const handleClick = useCallback((optionValue: any) => onChange(optionValue), [onChange]);

    const containerClass = ['radio-item-group', fullWidth ? 'full-width' : '', className].filter(Boolean).join(' ');

    return (
        <div className={containerClass} role="radiogroup" aria-disabled={disabled || undefined}>
            {options.map((option) => (
                <RadioItem
                    key={String(option.value)}
                    selected={option.value === value}
                    label={option.label}
                    size={size}
                    disabled={disabled || (option.disabled ?? false)}
                    onClick={() => handleClick(option.value)}
                />
            ))}
        </div>
    );
});

export default RadioItemGroup;
